import { useState, useEffect, useCallback, useRef } from 'react';

const parsePositiveInt = (rawValue, fallback) => {
  const parsed = Number.parseInt(String(rawValue || ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const MAX_PREDICTION_CONCURRENCY = parsePositiveInt(process.env.REACT_APP_PREDICTION_MAX_CONCURRENCY, 2);
const PREDICTION_RETRY_DELAYS_MS = [1200, 2500, 5000];
const MAX_PREDICTION_RETRY_ATTEMPTS = PREDICTION_RETRY_DELAYS_MS.length;
const MAX_ADVISORY_RETRY_ATTEMPTS = parsePositiveInt(process.env.REACT_APP_ADVISORY_MAX_RETRIES, 3);
const BACKGROUND_RETRY_DELAY_MS = 30000;
const RETRY_JITTER_MS = 300;
const RISK_INDICATORS_CACHE_TTL_MS = 30 * 60 * 1000;
const ANALYTICS_RETRY_DELAYS_MS = [3000, 7000, 12000];
const SUMMARY_REQUEST_TIMEOUT_MS = parsePositiveInt(process.env.REACT_APP_PREDICTION_SUMMARY_TIMEOUT_MS, 120000);
const ADVISORY_REQUEST_TIMEOUT_MS = parsePositiveInt(process.env.REACT_APP_PREDICTION_ADVISORY_TIMEOUT_MS, 180000);

export const useApi = (apiClient, refreshToken) => {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [charts, setCharts] = useState({});
  const [unitPredictions, setUnitPredictions] = useState({});
  const [unitPredictionMeta, setUnitPredictionMeta] = useState({});
  const [seasonalAnalysis, setSeasonalAnalysis] = useState(null);
  const [monthlyTrends, setMonthlyTrends] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});

  // Use refs to track last call times to prevent infinite re-renders
  const lastCallTimeRef = useRef({});
  const isInitializedRef = useRef(false);
  const predictionQueueRef = useRef([]);
  const predictionQueueKeysRef = useRef(new Set());
  const predictionInFlightKeysRef = useRef(new Set());
  const predictionInFlightCountRef = useRef(0);
  const predictionRetryTimersRef = useRef({});
  const analyticsRetryTimersRef = useRef({});
  const predictionLoadingCountRef = useRef({});
  const isUnmountedRef = useRef(false);
  const predictionsTabActiveRef = useRef(false);
  const seasonalRetryTimerRef = useRef(null);
  const seasonalErrorCooldownUntilRef = useRef(0);
  const unitPredictionMetaRef = useRef({});

  // Use the passed apiClient directly

  const setLoading = useCallback((key, isLoading) => {
    setLoadingStates(prev => ({ ...prev, [key]: isLoading }));
  }, []);

  const setUnitLoading = useCallback((unitName, isLoading) => {
    if (!unitName) return;
    const key = `intelligent_${unitName}`;
    const current = predictionLoadingCountRef.current[unitName] || 0;
    const next = isLoading ? current + 1 : Math.max(0, current - 1);
    predictionLoadingCountRef.current[unitName] = next;
    setLoading(key, next > 0);
  }, [setLoading]);

  const updatePredictionMeta = useCallback((unitName, patch) => {
    if (!unitName) return;
    setUnitPredictionMeta((prev) => {
      const current = prev[unitName] || {
        status: 'idle',
        attemptCount: 0,
        nextRetryAt: null,
        source: null,
        lastErrorCode: null,
      };
      return {
        ...prev,
        [unitName]: {
          ...current,
          ...(typeof patch === 'function' ? patch(current) : patch),
        },
      };
    });
  }, []);

  const extractApiErrorInfo = useCallback((error) => {
    const status = error?.response?.status ?? null;
    const detail = error?.response?.data?.detail;
    const code = detail?.code || error?.response?.data?.code || null;
    const message = String(
      detail?.error ||
      error?.response?.data?.error ||
      error?.message ||
      ''
    );
    const lowerMsg = message.toLowerCase();
    const networkLike = (
      !status ||
      lowerMsg.includes('timeout') ||
      lowerMsg.includes('network') ||
      lowerMsg.includes('failed to fetch') ||
      lowerMsg.includes('err_network')
    );
    return { status, code, message, networkLike };
  }, []);

  const isRetryableAnalyticsError = useCallback((info) => {
    if (!info) return false;
    if (info.networkLike) return true;
    if (info.status === 429) return true;
    if (info.status === 502) return true;
    if (info.status === 503) return true;
    if (info.status === 504) return true;
    if (info.code === 'initializing') return true;
    return false;
  }, []);

  const isRetryablePredictionError = useCallback((info) => {
    return isRetryableAnalyticsError(info);
  }, [isRetryableAnalyticsError]);

  const withRetryJitter = useCallback((baseDelay) => {
    const jitter = Math.floor(Math.random() * (RETRY_JITTER_MS * 2 + 1)) - RETRY_JITTER_MS;
    return Math.max(500, baseDelay + jitter);
  }, []);

  const clearPredictionRetryTimer = useCallback((retryKey) => {
    const timer = predictionRetryTimersRef.current[retryKey];
    if (timer) {
      clearTimeout(timer);
      delete predictionRetryTimersRef.current[retryKey];
    }
  }, []);

  const clearAnalyticsRetryTimer = useCallback((retryKey) => {
    const timer = analyticsRetryTimersRef.current[retryKey];
    if (timer) {
      clearTimeout(timer);
      delete analyticsRetryTimersRef.current[retryKey];
    }
  }, []);

  const scheduleAnalyticsRetry = useCallback((retryKey, retryAttempt, retryFn) => {
    if (!retryKey || typeof retryFn !== 'function') return;
    clearAnalyticsRetryTimer(retryKey);
    const index = Math.min(Math.max(0, retryAttempt), ANALYTICS_RETRY_DELAYS_MS.length - 1);
    const baseDelay = ANALYTICS_RETRY_DELAYS_MS[index] ?? BACKGROUND_RETRY_DELAY_MS;
    const retryDelay = withRetryJitter(baseDelay);

    analyticsRetryTimersRef.current[retryKey] = setTimeout(() => {
      delete analyticsRetryTimersRef.current[retryKey];
      if (isUnmountedRef.current) return;
      retryFn();
    }, retryDelay);
  }, [clearAnalyticsRetryTimer, withRetryJitter]);

  useEffect(() => {
    unitPredictionMetaRef.current = unitPredictionMeta;
  }, [unitPredictionMeta]);

  useEffect(() => {
    // React StrictMode remounts effects in development; reset mounted state on setup.
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
      Object.values(predictionRetryTimersRef.current).forEach((timer) => clearTimeout(timer));
      predictionRetryTimersRef.current = {};
      Object.values(analyticsRetryTimersRef.current).forEach((timer) => clearTimeout(timer));
      analyticsRetryTimersRef.current = {};
      if (seasonalRetryTimerRef.current) {
        clearTimeout(seasonalRetryTimerRef.current);
        seasonalRetryTimerRef.current = null;
      }
    };
  }, []);

  const checkHealth = useCallback(async () => {
    try {
      setLoading('health', true);
      const response = await apiClient.get('/health');
      setHealth(response.data);
      console.log('Backend health check successful:', response.data);
    } catch (error) {
      console.error('Health check failed:', error);
      setHealth({ status: 'unhealthy', error: error.message });
    } finally {
      setLoading('health', false);
    }
  }, [setLoading, apiClient]);

  const fetchStats = useCallback(async (forceRefresh = false) => {
    // Check cache first (60 minute TTL)
    const CACHE_KEY = 'stats_cache';
    const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours since data syncs hourly

    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < CACHE_TTL) {
            setStats(data);
            console.log('Stats loaded from cache');
            return;
          }
        }
      } catch (e) {
        console.warn('Failed to read stats cache:', e);
      }
    }

    try {
      setLoading('stats', true);
      const response = await apiClient.get('/stats');
      setStats(response.data);

      // Cache the result
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Failed to cache stats:', e);
      }

      console.log('Stats fetched successfully:', response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading('stats', false);
    }
  }, [setLoading, apiClient]);

  const fetchChartData = useCallback(async (chartType, unitName = null, forceRefresh = false, options = {}) => {
    const retryAttempt = Number(options?.retryAttempt || 0);
    const skipDebounce = Boolean(options?.skipDebounce);
    const retryKey = `chart:${chartType}:${unitName || 'all'}`;

    // Cache key includes unit name if provided
    const CACHE_KEY = `chart_${chartType}${unitName ? `_${unitName}` : ''}_cache`;
    const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours since data syncs hourly

    if (!options?.backgroundRetry) {
      clearAnalyticsRetryTimer(retryKey);
    }

    // Check cache first
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < CACHE_TTL) {
            setCharts(prev => ({ ...prev, [chartType]: data }));
            console.log(`${chartType} chart loaded from cache`);
            return;
          }
        }
      } catch (e) {
        console.warn(`Failed to read ${chartType} chart cache:`, e);
      }
    }

    // Debounce: prevent calls within 3 seconds of the last call for the same chart
    const now = Date.now();
    const lastCall = lastCallTimeRef.current[chartType] || 0;
    if (!skipDebounce && now - lastCall < 3000) {
      console.log(`Skipping ${chartType} fetch - too soon since last call`);
      return;
    }

    lastCallTimeRef.current[chartType] = now;

    try {
      setLoading(`chart_${chartType}`, true);
      let url = `/charts/${chartType}`;
      if (unitName) {
        url += `?unit_name=${encodeURIComponent(unitName)}`;
      }
      const response = await apiClient.get(url);
      setCharts(prev => ({ ...prev, [chartType]: response.data }));

      // Cache the result only if it's not an error response
      if (!response.data.error) {
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: response.data,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn(`Failed to cache ${chartType} chart:`, e);
        }
      }

      console.log(`${chartType} chart data fetched:`, response.data);
    } catch (error) {
      console.error(`Failed to fetch ${chartType} chart:`, error);
      const info = extractApiErrorInfo(error);
      const shouldRetry = isRetryableAnalyticsError(info) && retryAttempt < ANALYTICS_RETRY_DELAYS_MS.length;
      if (shouldRetry) {
        scheduleAnalyticsRetry(retryKey, retryAttempt, () => {
          fetchChartData(chartType, unitName, true, {
            retryAttempt: retryAttempt + 1,
            skipDebounce: true,
            backgroundRetry: true,
          });
        });
        return;
      }
      setCharts(prev => ({ ...prev, [chartType]: { error: error.message } }));
    } finally {
      setLoading(`chart_${chartType}`, false);
    }
  }, [
    setLoading,
    apiClient,
    clearAnalyticsRetryTimer,
    extractApiErrorInfo,
    isRetryableAnalyticsError,
    scheduleAnalyticsRetry,
  ]);

  const fetchUnitPrediction = useCallback(async (unitName, forceRefresh = false) => {
    const CACHE_KEY = `prediction_${unitName}_cache`;
    const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours since data syncs hourly

    // Check cache first
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < CACHE_TTL) {
            setUnitPredictions(prev => ({ ...prev, [unitName]: data }));
            console.log(`Prediction for ${unitName} loaded from cache`);
            return;
          }
        }
      } catch (e) {
        console.warn(`Failed to read prediction cache for ${unitName}:`, e);
      }
    }

    // Debounce: prevent calls within 3 seconds of the last call for the same unit
    const now = Date.now();
    const lastCall = lastCallTimeRef.current[`prediction_${unitName}`] || 0;
    if (now - lastCall < 3000) {
      console.log(`Skipping prediction fetch for ${unitName} - too soon since last call`);
      return;
    }

    lastCallTimeRef.current[`prediction_${unitName}`] = now;

    try {
      setLoading(`prediction_${unitName}`, true);
      const response = await apiClient.get(`/predict/unit/${unitName}`);
      setUnitPredictions(prev => ({ ...prev, [unitName]: response.data }));

      // Cache the result
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn(`Failed to cache prediction for ${unitName}:`, e);
      }

      console.log(`Unit prediction for ${unitName}:`, response.data);
    } catch (error) {
      console.error(`Failed to fetch prediction for ${unitName}:`, error);
      setUnitPredictions(prev => ({ ...prev, [unitName]: { error: error.message } }));
    } finally {
      setLoading(`prediction_${unitName}`, false);
    }
  }, [setLoading, apiClient]);

  const fetchSeasonalAnalysis = useCallback(async (forceRefresh = false, options = {}) => {
    const isStartupReady = options?.startupReady !== false;
    const retryAttempt = Number(options?.retryAttempt || 0);
    const CACHE_KEY = 'seasonal_analysis_cache';
    const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours since data syncs hourly

    if (!isStartupReady) {
      return;
    }

    if (!forceRefresh && seasonalErrorCooldownUntilRef.current > Date.now()) {
      return;
    }

    if (seasonalRetryTimerRef.current) {
      clearTimeout(seasonalRetryTimerRef.current);
      seasonalRetryTimerRef.current = null;
    }

    // Check cache first
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < CACHE_TTL) {
            setSeasonalAnalysis(data);
            console.log('Seasonal analysis loaded from cache');
            // Keep cache visible immediately, then refresh in background.
            if (!options?.backgroundRefresh) {
              setTimeout(() => {
                fetchSeasonalAnalysis(true, { startupReady: true, backgroundRefresh: true, retryAttempt: 0 });
              }, 0);
            }
            return;
          }
        }
      } catch (e) {
        console.warn('Failed to read seasonal analysis cache:', e);
      }
    }

    // Debounce: prevent calls within 5 seconds of the last call
    const now = Date.now();
    const lastCall = lastCallTimeRef.current['seasonal'] || 0;
    if (now - lastCall < 5000) {
      console.log('Skipping seasonal analysis fetch - too soon since last call');
      return;
    }

    lastCallTimeRef.current['seasonal'] = now;

    try {
      setLoading('seasonal', true);
      const response = await apiClient.get('/analysis/seasonal');
      setSeasonalAnalysis(response.data);
      seasonalErrorCooldownUntilRef.current = 0;

      // Cache the result
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Failed to cache seasonal analysis:', e);
      }

      console.log('Seasonal analysis fetched:', response.data);
    } catch (error) {
      console.error('Failed to fetch seasonal analysis:', error);
      const errInfo = extractApiErrorInfo(error);
      if (isRetryablePredictionError(errInfo)) {
        const nextBaseDelay = retryAttempt < PREDICTION_RETRY_DELAYS_MS.length
          ? PREDICTION_RETRY_DELAYS_MS[retryAttempt]
          : BACKGROUND_RETRY_DELAY_MS;
        const nextDelay = withRetryJitter(nextBaseDelay);
        seasonalRetryTimerRef.current = setTimeout(() => {
          fetchSeasonalAnalysis(true, {
            startupReady: true,
            retryAttempt: retryAttempt + 1,
            backgroundRefresh: true,
          });
        }, nextDelay);
        if (!seasonalAnalysis) {
          setSeasonalAnalysis({ initializing: true });
        }
      } else {
        seasonalErrorCooldownUntilRef.current = Date.now() + 120000;
        setSeasonalAnalysis({ error: error.message });
      }
    } finally {
      setLoading('seasonal', false);
    }
  }, [
    setLoading,
    apiClient,
    extractApiErrorInfo,
    isRetryablePredictionError,
    seasonalAnalysis,
    withRetryJitter,
  ]);

  const fetchMonthlyTrends = useCallback(async (forceRefresh = false, options = {}) => {
    const retryAttempt = Number(options?.retryAttempt || 0);
    const skipDebounce = Boolean(options?.skipDebounce);
    const retryKey = 'monthlyTrends';
    const CACHE_KEY = 'monthly_trends_cache';
    const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours since data syncs hourly

    if (!options?.backgroundRetry) {
      clearAnalyticsRetryTimer(retryKey);
    }

    // Check cache first 
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          const unitCount = data?.unit_highlights?.length ?? Object.keys(data?.unit_monthly_data ?? {}).length ?? 0;
          const cacheLooksStale = unitCount <= 1; // Old bug returned only one unit; treat as stale
          if (age < CACHE_TTL && !cacheLooksStale) {
            setMonthlyTrends(data);
            console.log('Monthly trends loaded from cache');
            return;
          }
          if (cacheLooksStale) {
            localStorage.removeItem(CACHE_KEY);
          }
        }
      } catch (e) {
        console.warn('Failed to read monthly trends cache:', e);
      }
    }

    // Debounce: prevent calls within 5 seconds of the last call
    const now = Date.now();
    const lastCall = lastCallTimeRef.current['monthlyTrends'] || 0;
    if (!skipDebounce && now - lastCall < 5000) {
      console.log('Skipping monthly trends fetch - too soon since last call');
      return;
    }

    lastCallTimeRef.current['monthlyTrends'] = now;

    try {
      setLoading('monthlyTrends', true);
      console.log('Starting monthly trends fetch...');
      const response = await apiClient.get('/analysis/monthly-trends');
      setMonthlyTrends(response.data);

      // Only cache the result if it's NOT an initializing state or error
      if (!response.data.initializing && !response.data.error) {
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: response.data,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('Failed to cache monthly trends:', e);
        }
      }

      console.log('Monthly trends analysis fetched:', response.data);
    } catch (error) {
      console.error('Failed to fetch monthly trends:', error);
      const info = extractApiErrorInfo(error);
      const shouldRetry = isRetryableAnalyticsError(info) && retryAttempt < ANALYTICS_RETRY_DELAYS_MS.length;
      if (shouldRetry) {
        if (!monthlyTrends) {
          setMonthlyTrends({ initializing: true });
        }
        scheduleAnalyticsRetry(retryKey, retryAttempt, () => {
          fetchMonthlyTrends(true, {
            retryAttempt: retryAttempt + 1,
            skipDebounce: true,
            backgroundRetry: true,
          });
        });
        return;
      }
      setMonthlyTrends({ error: error.message });
    } finally {
      setLoading('monthlyTrends', false);
    }
  }, [
    setLoading,
    apiClient,
    clearAnalyticsRetryTimer,
    extractApiErrorInfo,
    isRetryableAnalyticsError,
    monthlyTrends,
    scheduleAnalyticsRetry,
  ]);

  const applyRiskIndicators = useCallback((indicators = [], source = 'live') => {
    if (!Array.isArray(indicators) || indicators.length === 0) {
      return 0;
    }

    const normalizedIndicators = indicators
      .map((item) => ({
        ...item,
        unit: String(item?.unit || '').trim().toUpperCase(),
      }))
      .filter((item) => item.unit);

    if (normalizedIndicators.length === 0) {
      return 0;
    }

    setUnitPredictions((prev) => {
      const next = { ...prev };
      normalizedIndicators.forEach((indicator) => {
        const unitName = indicator.unit;
        const existing = next[unitName] || {};
        const existingLevel = String(existing.detail_level || '').toLowerCase();
        const hasDetailedPrediction = existingLevel === 'summary' || existingLevel === 'advisory';
        next[unitName] = {
          ...existing,
          ...indicator,
          detail_level: hasDetailedPrediction ? existing.detail_level : 'indicators',
          prediction_source: hasDetailedPrediction
            ? (existing.prediction_source || 'intelligent')
            : 'risk-indicators',
        };
      });
      return next;
    });

    setUnitPredictionMeta((prev) => {
      const next = { ...prev };
      normalizedIndicators.forEach((indicator) => {
        const unitName = indicator.unit;
        const existing = next[unitName] || {
          status: 'idle',
          attemptCount: 0,
          nextRetryAt: null,
          source: null,
          lastErrorCode: null,
        };
        const status = String(existing.status || '');
        const isSummaryReady = status === 'ready_summary' || status === 'ready_advisory';
        const isInFlight = ['queued', 'loading_summary', 'loading_advisory', 'retry_wait'].includes(status);
        next[unitName] = {
          ...existing,
          status: isSummaryReady ? status : (isInFlight ? status : 'ready_indicator'),
          source: isSummaryReady ? (existing.source || source) : source,
          nextRetryAt: null,
          lastErrorCode: null,
        };
      });
      return next;
    });

    return normalizedIndicators.length;
  }, []);

  const fetchRiskIndicators = useCallback(async (options = {}) => {
    const maintenanceType = String(options?.maintenanceType || 'unplanned').toLowerCase();
    const forceRefresh = Boolean(options?.forceRefresh);
    const retryAttempt = Number(options?.retryAttempt || 0);
    const requestedUnits = Array.isArray(options?.units)
      ? options.units
        .map((unit) => String(unit || '').trim().toUpperCase())
        .filter(Boolean)
      : [];
    const unitsScope = requestedUnits.length > 0 ? requestedUnits.join(',') : 'all';
    const cacheKey = `risk_indicators_${maintenanceType}_${unitsScope}_cache`;
    const retryKey = `riskIndicators:${maintenanceType}:${unitsScope}`;

    if (!options?.backgroundRetry) {
      clearAnalyticsRetryTimer(retryKey);
    }

    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          const age = Date.now() - Number(parsed?.timestamp || 0);
          if (age < RISK_INDICATORS_CACHE_TTL_MS && parsed?.data) {
            applyRiskIndicators(parsed.data.indicators || [], 'cache');
            console.log('Risk indicators loaded from cache');
            return parsed.data;
          }
        }
      } catch (cacheErr) {
        console.warn('Failed to read risk indicators cache:', cacheErr);
      }
    }

    try {
      setLoading('riskIndicators', true);
      const params = { maintenance_type: maintenanceType };
      if (requestedUnits.length > 0) {
        params.units = requestedUnits.join(',');
      }
      if (forceRefresh) {
        params.force_refresh = true;
      }

      const response = await apiClient.get('/predict/risk-indicators', { params });
      const payload = response.data || {};
      const source = payload?.cache?.hit ? 'cache' : 'live';
      applyRiskIndicators(payload.indicators || [], source);

      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          data: payload,
          timestamp: Date.now(),
        }));
      } catch (cacheErr) {
        console.warn('Failed to cache risk indicators:', cacheErr);
      }

      console.log('Risk indicators fetched successfully:', {
        maintenanceType,
        indicatorCount: Array.isArray(payload?.indicators) ? payload.indicators.length : 0,
        cacheHit: Boolean(payload?.cache?.hit),
      });
      return payload;
    } catch (error) {
      console.error('Failed to fetch risk indicators:', error);
      const info = extractApiErrorInfo(error);
      const shouldRetry = isRetryableAnalyticsError(info) && retryAttempt < ANALYTICS_RETRY_DELAYS_MS.length;
      if (shouldRetry) {
        scheduleAnalyticsRetry(retryKey, retryAttempt, () => {
          fetchRiskIndicators({
            ...options,
            forceRefresh: true,
            retryAttempt: retryAttempt + 1,
            backgroundRetry: true,
          }).catch((retryErr) => {
            console.warn('Risk indicators background retry failed:', retryErr);
          });
        });
        return { retrying: true, indicators: [] };
      }
      throw error;
    } finally {
      setLoading('riskIndicators', false);
    }
  }, [
    apiClient,
    applyRiskIndicators,
    clearAnalyticsRetryTimer,
    extractApiErrorInfo,
    isRetryableAnalyticsError,
    scheduleAnalyticsRetry,
    setLoading,
  ]);

  const processPredictionQueueRef = useRef(null);

  const executePredictionRequest = useCallback(async (item) => {
    const unitName = item?.unitName;
    const level = (item?.level || 'summary').toLowerCase();
    const attempt = Number(item?.attempt || 0);
    const forceRefresh = Boolean(item?.forceRefresh);
    if (!unitName) return;

    const retryKey = `${unitName}:${level}`;
    const cacheKey = `intelligent_prediction_v7_${unitName}_${level}_cache`;
    const statusLoading = level === 'advisory' ? 'loading_advisory' : 'loading_summary';
    const statusReady = level === 'advisory' ? 'ready_advisory' : 'ready_summary';

    updatePredictionMeta(unitName, {
      status: statusLoading,
      attemptCount: attempt + 1,
      nextRetryAt: null,
      lastErrorCode: null,
    });

    setUnitLoading(unitName, true);
    lastCallTimeRef.current[`intelligent_${unitName}_${level}`] = Date.now();

    try {
      const requestTimeout = level === 'advisory'
        ? ADVISORY_REQUEST_TIMEOUT_MS
        : SUMMARY_REQUEST_TIMEOUT_MS;
      const response = await apiClient.get(`/predict/intelligent/${unitName}`, {
        timeout: requestTimeout,
        params: {
          ...(forceRefresh ? { force_refresh: true } : {}),
          detail_level: level,
        },
      });

      setUnitPredictions((prev) => {
        const existing = prev[unitName] || {};
        if (level === 'summary' && existing.detail_level === 'advisory' && !forceRefresh) {
          return prev;
        }
        return {
          ...prev,
          [unitName]: level === 'advisory'
            ? { ...existing, ...response.data, detail_level: 'advisory' }
            : { ...existing, ...response.data },
        };
      });

      updatePredictionMeta(unitName, {
        status: statusReady,
        source: 'live',
        attemptCount: attempt + 1,
        nextRetryAt: null,
        lastErrorCode: null,
      });

      clearPredictionRetryTimer(retryKey);
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          data: response.data,
          timestamp: Date.now(),
        }));
      } catch (cacheErr) {
        console.warn(`Failed to cache intelligent prediction for ${unitName}:`, cacheErr);
      }
    } catch (error) {
      const info = extractApiErrorInfo(error);
      const retryable = isRetryablePredictionError(info);
      const nextAttempt = attempt + 1;
      const errorCode = info.code || (info.status ? String(info.status) : 'network_error');

      console.error(`Failed to fetch intelligent prediction for ${unitName} (${level}):`, error);
      if (retryable) {
        const maxRetryAttempts = level === 'advisory'
          ? MAX_ADVISORY_RETRY_ATTEMPTS
          : MAX_PREDICTION_RETRY_ATTEMPTS;
        const shouldRetry = attempt < maxRetryAttempts;
        if (!shouldRetry || !predictionsTabActiveRef.current) {
          updatePredictionMeta(unitName, {
            status: shouldRetry ? 'error_transient' : 'error_terminal',
            attemptCount: nextAttempt,
            source: unitPredictionMetaRef.current[unitName]?.source || null,
            lastErrorCode: errorCode,
            nextRetryAt: null,
          });
          return;
        }

        const retryBaseDelay = PREDICTION_RETRY_DELAYS_MS[attempt];
        const retryDelay = withRetryJitter(retryBaseDelay);
        const nextRetryAt = Date.now() + retryDelay;
        updatePredictionMeta(unitName, {
          status: 'error_transient',
          attemptCount: nextAttempt,
          source: unitPredictionMetaRef.current[unitName]?.source || null,
          lastErrorCode: errorCode,
          nextRetryAt,
        });
        updatePredictionMeta(unitName, {
          status: 'retry_wait',
          attemptCount: nextAttempt,
          source: unitPredictionMetaRef.current[unitName]?.source || null,
          lastErrorCode: errorCode,
          nextRetryAt,
        });

        clearPredictionRetryTimer(retryKey);
        predictionRetryTimersRef.current[retryKey] = setTimeout(() => {
          delete predictionRetryTimersRef.current[retryKey];
          const queueKey = `${unitName}:${level}`;
          if (predictionQueueKeysRef.current.has(queueKey) || predictionInFlightKeysRef.current.has(queueKey)) {
            return;
          }
          predictionQueueRef.current.push({
            unitName,
            level,
            attempt: nextAttempt,
            forceRefresh: true,
            priority: true,
          });
          predictionQueueKeysRef.current.add(queueKey);
          updatePredictionMeta(unitName, {
            status: 'queued',
            attemptCount: nextAttempt,
            lastErrorCode: errorCode,
            nextRetryAt,
          });
          if (processPredictionQueueRef.current) {
            processPredictionQueueRef.current();
          }
        }, retryDelay);
      } else {
        updatePredictionMeta(unitName, {
          status: 'error_terminal',
          attemptCount: nextAttempt,
          source: unitPredictionMetaRef.current[unitName]?.source || null,
          lastErrorCode: errorCode,
          nextRetryAt: null,
        });
        setUnitPredictions((prev) => ({
          ...prev,
          [unitName]: { ...(prev[unitName] || {}), error: info.message || error.message || 'Prediction unavailable' },
        }));
      }
    } finally {
      setUnitLoading(unitName, false);
    }
  }, [
    apiClient,
    clearPredictionRetryTimer,
    extractApiErrorInfo,
    isRetryablePredictionError,
    setUnitLoading,
    updatePredictionMeta,
    withRetryJitter,
  ]);

  const processPredictionQueue = useCallback(() => {
    if (isUnmountedRef.current) return;

    while (
      predictionInFlightCountRef.current < MAX_PREDICTION_CONCURRENCY &&
      predictionQueueRef.current.length > 0
    ) {
      const item = predictionQueueRef.current.shift();
      const unitName = item?.unitName;
      const level = (item?.level || 'summary').toLowerCase();
      if (!unitName) continue;
      const queueKey = `${unitName}:${level}`;
      predictionQueueKeysRef.current.delete(queueKey);
      if (predictionInFlightKeysRef.current.has(queueKey)) continue;

      predictionInFlightKeysRef.current.add(queueKey);
      predictionInFlightCountRef.current += 1;

      executePredictionRequest(item).finally(() => {
        predictionInFlightKeysRef.current.delete(queueKey);
        predictionInFlightCountRef.current = Math.max(0, predictionInFlightCountRef.current - 1);
        if (!isUnmountedRef.current && processPredictionQueueRef.current) {
          processPredictionQueueRef.current();
        }
      });
    }
  }, [executePredictionRequest]);

  useEffect(() => {
    processPredictionQueueRef.current = processPredictionQueue;
    // Drain any items queued before the queue processor ref was initialized.
    processPredictionQueue();
  }, [processPredictionQueue]);

  const setPredictionsTabActive = useCallback((isActive) => {
    predictionsTabActiveRef.current = Boolean(isActive);
    if (predictionsTabActiveRef.current && processPredictionQueueRef.current) {
      processPredictionQueueRef.current();
    }
  }, []);

  const enqueuePredictionRequest = useCallback((payload) => {
    const unitName = payload?.unitName;
    const level = (payload?.level || 'summary').toLowerCase();
    const attempt = Number(payload?.attempt || 0);
    const forceRefresh = Boolean(payload?.forceRefresh);
    const priority = Boolean(payload?.priority);

    if (!unitName) return;
    const queueKey = `${unitName}:${level}`;
    const meta = unitPredictionMetaRef.current[unitName] || {};

    if (
      !forceRefresh &&
      meta.source === 'live' &&
      (
        (level === 'summary' && ['ready_summary', 'ready_advisory'].includes(meta.status)) ||
        (level === 'advisory' && meta.status === 'ready_advisory')
      )
    ) {
      return;
    }

    if (!forceRefresh) {
      const cacheKey = `intelligent_prediction_v7_${unitName}_${level}_cache`;
      const cacheTtl = 4 * 60 * 60 * 1000;
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < cacheTtl) {
            setUnitPredictions((prev) => {
              const existing = prev[unitName] || {};
              if (level === 'summary' && existing.detail_level === 'advisory') {
                return prev;
              }
              return {
                ...prev,
                [unitName]: level === 'advisory'
                  ? { ...existing, ...data, detail_level: 'advisory' }
                  : { ...existing, ...data },
              };
            });
            updatePredictionMeta(unitName, {
              status: level === 'advisory' ? 'ready_advisory' : 'ready_summary',
              source: 'cache',
              attemptCount: meta.attemptCount || 0,
            });
          }
        }
      } catch (cacheErr) {
        console.warn(`Failed to read intelligent prediction cache for ${unitName}:`, cacheErr);
      }
    }

    if (predictionQueueKeysRef.current.has(queueKey) || predictionInFlightKeysRef.current.has(queueKey)) {
      return;
    }

    const request = { unitName, level, attempt, forceRefresh, priority };
    if (priority) {
      predictionQueueRef.current.unshift(request);
    } else {
      predictionQueueRef.current.push(request);
    }
    predictionQueueKeysRef.current.add(queueKey);

    updatePredictionMeta(unitName, {
      status: 'queued',
      attemptCount: attempt,
      nextRetryAt: null,
      lastErrorCode: null,
      source: meta.source || null,
    });

    if (processPredictionQueueRef.current) {
      processPredictionQueueRef.current();
    } else {
      // Safety fallback for first-render timing races.
      setTimeout(() => {
        if (processPredictionQueueRef.current) {
          processPredictionQueueRef.current();
        }
      }, 0);
    }
  }, [updatePredictionMeta]);

  const queueIntelligentPredictions = useCallback((units = [], options = {}) => {
    if (!Array.isArray(units) || units.length === 0) return;
    const level = (options?.detailLevel || 'summary').toLowerCase();
    const priorityUnits = Array.isArray(options?.priorityUnits) ? options.priorityUnits : [];
    const seen = new Set();
    const orderedUnits = [...priorityUnits, ...units].filter((unit) => {
      if (!unit || seen.has(unit)) return false;
      seen.add(unit);
      return true;
    });
    orderedUnits.forEach((unit, idx) => {
      enqueuePredictionRequest({
        unitName: unit,
        level,
        forceRefresh: Boolean(options?.forceRefresh),
        priority: idx < priorityUnits.length,
        attempt: 0,
      });
    });
  }, [enqueuePredictionRequest]);

  const fetchIntelligentUnitPrediction = useCallback((unitName, forceRefresh = false, detailLevel = 'summary') => {
    enqueuePredictionRequest({
      unitName,
      forceRefresh,
      level: detailLevel,
      priority: true,
      attempt: 0,
    });
  }, [enqueuePredictionRequest]);

  const fetchHeatmapData = useCallback(async (maintenanceType = 'unplanned', forceRefresh = false, options = {}) => {
    const retryAttempt = Number(options?.retryAttempt || 0);
    const skipDebounce = Boolean(options?.skipDebounce);
    const retryKey = `heatmap:${maintenanceType}`;
    const CACHE_KEY = `heatmap_${maintenanceType}_cache`;
    const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours since data syncs hourly

    if (!options?.backgroundRetry) {
      clearAnalyticsRetryTimer(retryKey);
    }

    // Check cache first
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < CACHE_TTL) {
            setHeatmapData(data);
            console.log(`Heatmap for ${maintenanceType} loaded from cache`);
            return;
          }
        }
      } catch (e) {
        console.warn(`Failed to read heatmap cache for ${maintenanceType}:`, e);
      }
    }

    // Debounce: prevent calls within 5 seconds of the last call
    const now = Date.now();
    const lastCall = lastCallTimeRef.current[`heatmap_${maintenanceType}`] || 0;
    if (!skipDebounce && now - lastCall < 5000) {
      console.log(`Skipping heatmap fetch for ${maintenanceType} - too soon since last call`);
      return;
    }

    lastCallTimeRef.current[`heatmap_${maintenanceType}`] = now;

    try {
      setLoading('heatmap', true);
      console.log(`Starting heatmap fetch for ${maintenanceType} maintenance...`);
      const response = await apiClient.get(`/analysis/heatmap?maintenance_type=${maintenanceType}`);
      setHeatmapData(response.data);

      // Cache the result
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn(`Failed to cache heatmap for ${maintenanceType}:`, e);
      }

      console.log('Heatmap analysis fetched:', response.data);
    } catch (error) {
      console.error('Failed to fetch heatmap analysis:', error);
      const info = extractApiErrorInfo(error);
      const shouldRetry = isRetryableAnalyticsError(info) && retryAttempt < ANALYTICS_RETRY_DELAYS_MS.length;
      if (shouldRetry) {
        scheduleAnalyticsRetry(retryKey, retryAttempt, () => {
          fetchHeatmapData(maintenanceType, true, {
            retryAttempt: retryAttempt + 1,
            skipDebounce: true,
            backgroundRetry: true,
          });
        });
        return;
      }
      setHeatmapData({ error: error.message });
    } finally {
      setLoading('heatmap', false);
    }
  }, [
    setLoading,
    apiClient,
    clearAnalyticsRetryTimer,
    extractApiErrorInfo,
    isRetryableAnalyticsError,
    scheduleAnalyticsRetry,
  ]);

  const fetchAvailableUnits = useCallback(async (forceRefresh = false) => {
    const CACHE_KEY = 'available_units_cache';
    const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours since data syncs hourly

    // Check cache first
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < CACHE_TTL) {
            setAvailableUnits(data);
            console.log('Available units loaded from cache');
            return;
          }
        }
      } catch (e) {
        console.warn('Failed to read available units cache:', e);
      }
    }

    try {
      setLoading('availableUnits', true);
      const response = await apiClient.get('/units/available');
      if (response.data.units) {
        setAvailableUnits(response.data.units);

        // Cache the result
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: response.data.units,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('Failed to cache available units:', e);
        }

        console.log('Available units fetched successfully:', response.data.units);
      }
    } catch (error) {
      console.error('Failed to fetch available units:', error);
      // Keep existing state on error; avoid showing a misleading hardcoded subset.
    } finally {
      setLoading('availableUnits', false);
    }
  }, [setLoading, apiClient]);

  const submitQuery = useCallback(async (query) => {
    try {
      setLoading('query', true);
      // Use /chat endpoint instead of /query for unified chatbot engine
      const response = await apiClient.post('/chat', { query: query }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('Chat response:', response.data);

      // /chat returns: {query, response, rag_used}
      // Extract the response text
      if (response.data.response) {
        return response.data.response;
      } else if (response.data.error) {
        throw new Error(response.data.error);
      } else if (typeof response.data === 'string') {
        return response.data;
      } else {
        // Fallback: return formatted JSON if structure is unexpected
        return JSON.stringify(response.data, null, 2);
      }
    } catch (error) {
      console.error('Failed to submit query:', error);
      // Provide more detailed error message
      const errorMessage = error.response?.data?.error || error.message || 'Sorry, there was an error processing your query. Please try again.';
      throw new Error(errorMessage);
    } finally {
      setLoading('query', false);
    }
  }, [setLoading, apiClient]);

  // Streaming query with progress updates
  // Streaming query with progress updates
  // chatHistory: array of { role: 'user'|'assistant', content: string } messages
  const submitQueryStreaming = useCallback(async (query, onProgress, onComplete, onError, authToken = null, abortController = null, chatHistory = [], conversationId = null) => {
    const baseUrl = process.env.REACT_APP_API_URL || '';

    try {
      setLoading('query', true);

      // Get token from localStorage if not provided
      const token = authToken || localStorage.getItem('auth_token');

      const headers = {
        'Content-Type': 'application/json',
      };

      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Note: chatHistory is no longer sent to the backend as memory is handled natively via conversation_id

      const payload = {
        query
      };

      // Add conversation_id if provided
      if (conversationId) {
        payload.conversation_id = conversationId;
      }

      const response = await fetch(`${baseUrl}/chat/stream`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
        signal: abortController?.signal,
      });

      // Handle token expiration for streaming
      if (response.status === 401 && refreshToken && !abortController?.signal.aborted) {
        console.log('Streaming endpoint returned 401, attempting token refresh...');
        try {
          const newToken = await refreshToken();
          // Retry once with new token
          const retryHeaders = { ...headers, 'Authorization': `Bearer ${newToken}` };
          const retryResponse = await fetch(`${baseUrl}/chat/stream`, {
            method: 'POST',
            headers: retryHeaders,
            body: JSON.stringify(payload),
            signal: abortController?.signal,
          });

          if (!retryResponse.ok) {
            const errorText = await retryResponse.text().catch(() => 'Unknown error');
            throw new Error(`HTTP error! status: ${retryResponse.status}. ${errorText.substring(0, 100)}`);
          }

          // Use the retry response for the rest of the function
          return processStream(retryResponse);
        } catch (refreshErr) {
          console.error('Token refresh failed during streaming:', refreshErr);
          throw new Error('Authentication expired. Please log in again.');
        }
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error(`HTTP error! status: ${response.status}, body: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}. ${errorText.substring(0, 100)}`);
      }

      return processStream(response);

      async function processStream(resp) {
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        let currentEventType = null;
        let terminalDispatched = false;
        try {
          while (true) {
            // Check if aborted
            if (abortController?.signal.aborted) {
              reader.cancel().catch(() => { }); // Ignore cancel errors
              return; // Exit gracefully instead of throwing
            }

            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('event:')) {
                currentEventType = line.substring(6).trim();
                continue;
              }
              if (line.startsWith('data:')) {
                try {
                  const data = JSON.parse(line.substring(5).trim());

                  // Handle different event types based on the event name
                  if (currentEventType === 'response') {
                    // Final response event
                    if (data.response) {
                      if (!terminalDispatched) {
                        terminalDispatched = true;
                        onComplete && onComplete(data.response, data.metadata || null);
                      }
                    }
                  } else if (currentEventType === 'status') {
                    // Status/progress update
                    if (data.message) {
                      if (onProgress) {
                        onProgress(data);
                      }
                    }
                  } else if (currentEventType === 'error') {
                    // Error event
                    if (!terminalDispatched) {
                      terminalDispatched = true;
                      onError && onError(data.message || 'An error occurred');
                    }
                  } else if (currentEventType === 'done') {
                    // Stream complete - no action needed, response should have been sent
                    // But if we haven't received a response yet, check if there's an error
                    if (data.success === false && data.message) {
                      if (!terminalDispatched) {
                        terminalDispatched = true;
                        onError && onError(data.message);
                      }
                    }
                  } else {
                    // Fallback: try to infer from data structure
                    if (data.response) {
                      // Final response
                      if (!terminalDispatched) {
                        terminalDispatched = true;
                        onComplete && onComplete(data.response, data.metadata || null);
                      }
                    } else if (data.message) {
                      // Progress update
                      onProgress && onProgress(data);
                    } else if (data.success === false && data.message) {
                      // Error
                      if (!terminalDispatched) {
                        terminalDispatched = true;
                        onError && onError(data.message);
                      }
                    }
                  }
                } catch (e) {
                  // Ignore parse errors for incomplete data
                  console.warn('Failed to parse SSE data:', e, line);
                }
              }
            }
          }
        } catch (streamError) {
          // Handle stream read errors (e.g., abort)
          const isAbortError =
            streamError.name === 'AbortError' ||
            streamError.message?.includes('aborted') ||
            streamError.message?.includes('BodyStreamBuffer');

          if (isAbortError) {
            console.log('Stream read cancelled');
            return; // Exit gracefully
          }
          throw streamError; // Re-throw other errors
        }
      }
    } catch (error) {
      // Don't show error if request was cancelled by user
      const isAbortError =
        error.name === 'AbortError' ||
        error.message === 'Request cancelled by user' ||
        error.message?.includes('aborted') ||
        error.message?.includes('BodyStreamBuffer');

      if (isAbortError) {
        setLoading('query', false);
        console.log('Request cancelled by user');
        return; // Silently return, user intentionally cancelled
      }

      console.error('Streaming query failed:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });

      // Provide more specific error messages (avoid raw "timeout of 30000 ms exceeded" when window was left open)
      let errorMessage = 'Network error. ';
      if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        errorMessage += 'Cannot connect to backend server. Please check if the backend is running on ' + baseUrl;
      } else if (error.message?.includes('HTTP error')) {
        errorMessage = error.message;
      } else if (error.message?.toLowerCase().includes('timeout') && (error.message?.includes('exceeded') || error.message?.includes('ms'))) {
        errorMessage = 'The request took too long. The server may be starting up—please refresh and try again.';
      } else if (error.message?.includes('session has expired') || error.message?.includes('Authentication expired')) {
        errorMessage = error.message;
      } else {
        errorMessage += error.message || 'Failed to process query. Please try again.';
      }

      onError && onError(errorMessage);
    } finally {
      setLoading('query', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setLoading]);

  // Optional bootstrap on mount (disabled by default to avoid cold-start timeout cascades in cloud)
  useEffect(() => {
    const bootstrapOnMount = String(process.env.REACT_APP_API_BOOTSTRAP_ON_MOUNT || '').toLowerCase() === 'true';
    if (bootstrapOnMount && !isInitializedRef.current) {
      isInitializedRef.current = true;
      // Load these in parallel for faster initial load (don't await - let them run concurrently)
      checkHealth().catch(err => console.warn('Health check failed:', err));
      fetchStats().catch(err => console.warn('Stats fetch failed:', err));
      fetchAvailableUnits().catch(err => console.warn('Available units fetch failed:', err));
    }
  }, [checkHealth, fetchStats, fetchAvailableUnits]);

  // Upload and analyze file
  const uploadAndAnalyzeFile = useCallback(async (file, query = '', authToken = null) => {
    try {
      setLoading('query', true);

      // Get token from localStorage if not provided
      const token = authToken || localStorage.getItem('auth_token');

      const formData = new FormData();
      formData.append('file', file);
      if (query) {
        formData.append('query', query);
      }

      const headers = {};

      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // File analysis can take 60s+ (PDF extraction + LLM); use 2 min timeout instead of default 30s
      const response = await apiClient.post('/chat/upload', formData, {
        timeout: 120000,
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.response) {
        return response.data.response;
      } else if (response.data.error) {
        throw new Error(response.data.error);
      } else {
        return JSON.stringify(response.data, null, 2);
      }
    } catch (error) {
      console.error('Failed to upload and analyze file:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Sorry, there was an error processing your file. Please try again.';
      throw new Error(errorMessage);
    } finally {
      setLoading('query', false);
    }
  }, [setLoading, apiClient]);

  const fetchStatsOverview = useCallback(async (forceRefresh = false) => {
    const CACHE_KEY = 'stats_overview_cache';
    const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours since data syncs hourly

    // Check cache first
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < CACHE_TTL) {
            console.log('Stats overview loaded from cache');
            return data;
          }
        }
      } catch (e) {
        console.warn('Failed to read stats overview cache:', e);
      }
    }

    try {
      const response = await apiClient.get('/stats/overview');
      const data = response.data;

      // Cache the result
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Failed to cache stats overview:', e);
      }

      console.log('Stats overview fetched successfully:', data);
      return data;
    } catch (error) {
      console.error('Error fetching stats overview:', error);
      throw error;
    }
  }, [apiClient]);

  const fetchStartupStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/startup/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching startup status:', error);
      // Important: do not masquerade transport/auth errors as "backend initializing".
      // The caller should decide how to handle a failed status request.
      return null;
    }
  }, [apiClient]);

  // Storage Hub API
  const fetchStorageFiles = useCallback(async () => {
    try {
      setLoading('storage', true);
      const response = await apiClient.get('/storage/files');
      return response.data;
    } catch (error) {
      console.error('Error fetching storage files:', error);
      throw error;
    } finally {
      setLoading('storage', false);
    }
  }, [setLoading, apiClient]);

  const uploadToStorage = useCallback(async (file, category) => {
    try {
      setLoading('storage', true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);

      const response = await apiClient.post('/storage/export', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading to storage:', error);
      throw error;
    } finally {
      setLoading('storage', false);
    }
  }, [setLoading, apiClient]);

  const deleteFromStorage = useCallback(async (filePath) => {
    try {
      setLoading('storage', true);
      // filePath is like "Reports/Fleet Event Counts/file.pdf"
      const response = await apiClient.delete(`/storage/files/${encodeURIComponent(filePath).replace(/%2F/g, '/')}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting from storage:', error);
      throw error;
    } finally {
      setLoading('storage', false);
    }
  }, [setLoading, apiClient]);

  const downloadFromStorage = useCallback(async (filePath) => {
    try {
      setLoading('storage', true);
      // filePath is like "Reports/Fleet Event Counts/file.pdf"
      // Use responseType: 'blob' to get raw data
      const response = await apiClient.get(`/storage/download/${encodeURIComponent(filePath).replace(/%2F/g, '/')}`, {
        responseType: 'blob'
      });

      // Create blob link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from filePath
      const fileName = filePath.split('/').pop();
      link.setAttribute('download', fileName);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error downloading from storage:', error);
      throw error;
    } finally {
      setLoading('storage', false);
    }
  }, [setLoading, apiClient]);

  return {
    stats,
    health,
    charts,
    unitPredictions,
    unitPredictionMeta,
    seasonalAnalysis,
    monthlyTrends,
    heatmapData,
    availableUnits,
    loadingStates,
    checkHealth,
    fetchStats,
    fetchChartData,
    fetchUnitPrediction,
    fetchSeasonalAnalysis,
    fetchMonthlyTrends,
    fetchRiskIndicators,
    fetchHeatmapData,
    fetchAvailableUnits,
    fetchIntelligentUnitPrediction,
    setPredictionsTabActive,
    queueIntelligentPredictions,
    submitQuery,
    submitQueryStreaming,
    uploadAndAnalyzeFile,
    fetchStatsOverview,
    fetchStartupStatus,
    fetchStorageFiles,
    uploadToStorage,
    deleteFromStorage,
    downloadFromStorage
  };
};
